import { AutoCoderPipeline, PipelineLifecycle } from '../types/gotypes'
import type { ThroughputDeficitPipeline } from '../types/autocoderThroughput'

/**
 * The compact queue strip on a pipeline LIST row.
 *
 * WHERE THE NUMBERS COME FROM, and what they are NOT.
 *
 * The list endpoint carries no coding counts, so the strip needs a second
 * source. The options were:
 *   a) one call per row to `/pipelines/:id/throughput` or `/pipelines/:id/queue`
 *      — an N+1 on a page that pages 50 rows at a time and polls every 5s;
 *   b) ONE call to the existing customer-scoped throughput endpoint, whose
 *      `deficit.pipelines[]` carries a per-pipeline uncoded/failed/live-batch
 *      count and the two gate flags for every pipeline at once.
 * (b) wins, and this model is written against it.
 *
 * THE TRAP THIS MODEL EXISTS TO AVOID. `deficit.records` is not "uncoded" in
 * the ordinary sense — `store_autocoder_throughput.go` defines it as uncoded
 * records that NO LIVE BATCH OWNS (`notInLiveBatchClause`). Everything sealed
 * into a pending or running batch is excluded BY CONSTRUCTION. So
 * `total_cases - deficit` is NOT a coded count: on a pipeline mid-run with all
 * 34,845 records sealed into pending batches the deficit is 0, the pipeline is
 * absent from the feed entirely, and that subtraction reports "34,845 coded"
 * before a single record has been near a model.
 *
 * This model therefore never claims a coded count at all. It reports:
 *   - what is WAITING, unsealed and unowned (the deficit), and which gate holds it;
 *   - how many batches are in flight;
 *   - the record total, when one is stored.
 *
 * Two further absences are honoured rather than guessed:
 *   - no feed at all (the endpoint is realm-admin gated) -> `known: false`;
 *   - ARCHIVED pipelines, which the deficit query filters out on purpose
 *     ("retired work, not an alarm") -> `known: false`. Absence cannot mean
 *     "nothing waiting" for a pipeline the query never looked at.
 *
 * `total_cases` is stored config and the streaming Ingest seam never writes it,
 * so long-lived pipelines routinely have 0. `totalKnown` says so, and the
 * caller draws no bar.
 */
// T19 (2026-08-12) retired the 'batching' gate this used to distinguish from
// 'coding' — coding is the only gate left. The union stays (rather than
// collapsing to boolean) so a future gate can slot back in without another
// signature change.
export type RowQueueGate = 'coding' | null

export interface RowQueueModel {
  /** False when the deficit feed is absent, or does not cover this pipeline. */
  known: boolean
  /** False when no record total is stored — draw no bar. */
  totalKnown: boolean
  /** Records the pipeline holds (config total, or the deficit if larger). */
  total: number
  /** Uncoded records that no live batch owns. NOT "everything uncoded". */
  waiting: number
  /** Of `waiting`, the part only an operator retry will clear. */
  failed: number
  liveBatches: number
  /** The gate holding those records back, if any. */
  heldBy: RowQueueGate
  /** One line, ready to render. */
  summary: string
}

const unknown = (total: number, tail?: string): RowQueueModel => ({
  known: false,
  totalKnown: total > 0,
  total,
  waiting: 0,
  failed: 0,
  liveBatches: 0,
  heldBy: null,
  summary: total > 0
    ? `${total.toLocaleString()} records${tail ? ` · ${tail}` : ''}`
    : (tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : 'No records yet'),
})

export const buildRowQueue = (
  pipeline: AutoCoderPipeline,
  deficitRows: ThroughputDeficitPipeline[] | undefined,
): RowQueueModel => {
  const totalCases = pipeline?.config?.total_cases || 0

  if (!deficitRows) return unknown(totalCases)

  // The deficit query excludes archived pipelines by design, so absence from
  // the list tells us nothing about one. Saying "nothing waiting" here would
  // report an archived pipeline holding 5,000 uncoded records as finished.
  if (pipeline?.lifecycle === PipelineLifecycle.archived) {
    return unknown(totalCases, 'archived, coding progress is not tracked')
  }

  const row = deficitRows.find(r => r.pipelineId === pipeline?.id)
  const waiting = row?.records ?? 0
  const failed = row?.failedRecords ?? 0
  const liveBatches = row?.liveBatches ?? 0

  // `total_cases` is stored config; the deficit is computed live. They can
  // disagree, and when they do the live number is the one we can defend.
  const total = Math.max(totalCases, waiting)

  const heldBy: RowQueueGate = row?.codingOff ? 'coding' : null

  const parts: string[] = []
  if (waiting > 0) {
    let head = `${waiting.toLocaleString()} waiting to be coded`
    if (failed > 0) head += ` (${failed.toLocaleString()} failed — needs retry)`
    else if (heldBy) head += ` — ${heldBy} is off`
    parts.push(head)
  }
  if (liveBatches > 0) {
    parts.push(`${liveBatches.toLocaleString()} ${liveBatches === 1 ? 'batch' : 'batches'} in flight`)
  }
  if (parts.length === 0) parts.push('Nothing waiting to be coded')
  if (totalCases > 0) parts.push(`${total.toLocaleString()} records`)

  return {
    known: true,
    totalKnown: totalCases > 0,
    total,
    waiting,
    failed,
    liveBatches,
    heldBy,
    summary: parts.join(' · '),
  }
}
